import NiceModal from "@ebay/nice-modal-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import {
    editInvestmentStrategyResultSelector,
    resetQuestionnaire,
    selectExistingFundsRows,
    selectNewFundsRows,
    setExistingFunds,
    setNewFunds,
    setStepByName,
    StepperIndexes,
    useAppDispatch,
    useAppSelector,
} from "@repo/logic";
import { FC, forwardRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useScrollFirstError, useTypedTranslation } from "../../../hooks";
import { FormErrorMessage } from "../../FormErrorMessage";
import { ProgressBarList } from "../../ProgressBarList";
import { DefaultStepProps } from "../../Stepper/CustomStepper";
import { BodyTextM } from "../../Typography";
import { PensionQuestionnaireStepProps } from "../PensionQuestionnaireStepper";
import { useCalculatedData, useFundsChanged, useNoChangeWarning } from "./EditInvestmentStrategy.helpers";
import { EditInvestmentStrategyMode } from "./EditInvestmentStrategy.types";
import { EditInvestmentStrategyConfirmBox } from "./EditInvestmentStrategyConfirmBox";
import { EditInvestmentStrategyFundAllocation } from "./EditInvestmentStrategyFundAllocation";
import * as Styled from "./EditInvestmentStrategyPage.styled";
import { EditInvestmentStrategyRemainingAllocation } from "./EditInvestmentStrategyRemainingAllocation";
import { useEditInvestmentStrategyValidationSchema } from "./EditInvestmentStrategyValidation";

export type EditInvestmentStrategyPageProps = {
    mode: EditInvestmentStrategyMode;
} & PensionQuestionnaireStepProps &
    DefaultStepProps;

export type FormValues = {
    funds: {
        id: number;
        allocation: string;
    }[];
    remaining: number;
    agree: boolean;
};

const defaultFundsCount = 3;

export const EditInvestmentStrategyPage: FC<EditInvestmentStrategyPageProps> = ({
    mode,
    queries: { questionnaireConfigQuery, usePensionDetail },
    setContinueCondition,
    contractId,
}) => {
    const { t } = useTypedTranslation();
    const dispatch = useAppDispatch();
    const contentRef = useRef<HTMLDivElement | null>(null);
    const investmentProfile = useAppSelector(editInvestmentStrategyResultSelector);
    const reduxExistingFundRows = useAppSelector(selectExistingFundsRows);
    const reduxNewFundRows = useAppSelector(selectNewFundsRows);
    const [noChangeConfirmed, setNoChangeConfirmed] = useState(false);
    const warningRef = useRef<HTMLDivElement | null>(null);

    const { data: pensionDetail, refetch: refetchPensionDetail } =
        usePensionDetail?.({ id: String(contractId), options: { enabled: Boolean(contractId) }, type: "UF" }) ?? {};

    const { data: config, refetch: refetchQuestionnaireConfig } = questionnaireConfigQuery ?? {};
    // fetch config if not available
    useEffect(() => {
        if (!config) refetchQuestionnaireConfig?.();
    }, [refetchQuestionnaireConfig, config]);

    //fetch pension detail if not available
    useEffect(() => {
        if (!pensionDetail) {
            refetchPensionDetail?.();
        }
    }, [pensionDetail, refetchPensionDetail]);

    const { currentSituationMemoized, data } = useCalculatedData(
        pensionDetail,
        config,
        mode,
        investmentProfile,
        reduxExistingFundRows,
        reduxNewFundRows,
    );

    const schema = useEditInvestmentStrategyValidationSchema(data.fundsWithAllocationRecommendations ?? []);

    const form = useForm<FormValues>({
        defaultValues: {
            funds: [],
            agree: false,
            remaining: 0,
        },
        mode: "onChange",
        resolver: yupResolver(schema),
    });

    const { handleSubmit, setValue, trigger, formState, watch } = form;
    const funds = watch("funds");
    const fundsChanged = useFundsChanged(pensionDetail, config, mode, funds);
    const { warning, shouldExitOnNext, shouldSkipToRecap } = useNoChangeWarning(mode, fundsChanged);

    useScrollFirstError<FormValues>(
        { formState },
        {
            behavior: "smooth",
            root: contentRef.current ?? undefined,
            topOffset: 100,
        },
        { auto: true, severities: ["error"] },
    );

    // Register the (stable) continue function with the parent only once (or when the setter changes)
    useEffect(() => {
        setContinueCondition?.(async () => {
            let isValid = false;

            // Při prvním pokusu (noChangeConfirmed je false)
            if (!noChangeConfirmed && (shouldExitOnNext || shouldSkipToRecap)) {
                // Scroll to error message
                warningRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                // Nastavit příznak, že uživatel byl upozorněn s mírným spožděním (pro lidi co používají double click)
                setTimeout(() => setNoChangeConfirmed(true), 500);
            }

            await handleSubmit(
                (data) => {
                    const dataWithHoutFundNames = data.funds.map((fund) => {
                        return {
                            id: fund.id,
                            value: Number(fund.allocation),
                        };
                    });
                    if (mode === "new") {
                        dispatch(
                            setNewFunds({
                                newFundRows: dataWithHoutFundNames,
                            }),
                        );
                    } else if (mode === "existing") {
                        dispatch(
                            setExistingFunds({
                                existingFundRows: dataWithHoutFundNames,
                            }),
                        );
                    }
                    isValid = true;
                },
                () => {
                    isValid = false;
                },
            )();

            if (!isValid) {
                return false;
            }

            if (!noChangeConfirmed && (shouldExitOnNext || shouldSkipToRecap)) return false;

            if (noChangeConfirmed && shouldExitOnNext) {
                // Close modal and reset questionnaire state
                NiceModal.hide("Nicemodal-PensionQuestionnaireStepperModal");
                dispatch(resetQuestionnaire());
                return true;
            }

            if (noChangeConfirmed && shouldSkipToRecap) {
                // Skip to recap step
                dispatch(setStepByName(StepperIndexes.changeContributionRecapPage));
                return false;
            }

            return true;
        });
    }, [
        setContinueCondition,
        dispatch,
        handleSubmit,
        config,
        trigger,
        mode,
        shouldExitOnNext,
        noChangeConfirmed,
        shouldSkipToRecap,
    ]);

    // Initialize form values with data from preferredSituation based on API or Redux if present
    useEffect(() => {
        if (formState.isDirty) return; // avoid overwriting user changes on the input fields
        setValue(
            "funds",
            data?.preferredSituation
                ?.slice()
                .sort((a, b) => a.id - b.id)
                .map((allocation) => ({
                    id: allocation.id,
                    allocation: allocation.value.toString(),
                })) ?? [],
        );

        setValue("agree", false);
    }, [data?.preferredSituation, setValue, formState.isDirty]);

    return (
        <div ref={contentRef}>
            <FormProvider {...form}>
                <form>
                    <Styled.InvestmentStrategyWrapperStack>
                        <Styled.InvestmentStrategyTitle>
                            {t(`investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.${mode}.title`)}
                        </Styled.InvestmentStrategyTitle>
                        <Styled.InvestmentStrategyTitleNote component="p">
                            {t(
                                `investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.${mode}.description`,
                            )}
                        </Styled.InvestmentStrategyTitleNote>

                        <Styled.GridWrapperBox>
                            <Styled.GridItemBox>
                                <Styled.InvestmentStrategyHeadingWrapperStack>
                                    <Styled.InvestmentStrategyHeadingS>
                                        {t(
                                            `investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.${mode}.currentAllocation`,
                                        )}
                                    </Styled.InvestmentStrategyHeadingS>
                                    {mode === "existing" && (
                                        <Box>
                                            <BodyTextM component="p">
                                                {t(
                                                    "investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.savings",
                                                )}
                                            </BodyTextM>
                                            <BodyTextM component="p">
                                                {currentSituationMemoized?.savings != null
                                                    ? `${currentSituationMemoized.savings.toLocaleString("cs-CZ", { style: "currency", currency: "CZK" })}`
                                                    : ""}
                                            </BodyTextM>
                                        </Box>
                                    )}
                                </Styled.InvestmentStrategyHeadingWrapperStack>
                            </Styled.GridItemBox>
                            <Styled.GridItemBox>
                                <ProgressBarList
                                    rows={data.progressBarRows}
                                    isLoading={!data.progressBarRows || data.progressBarRows.length === 0}
                                    loadingRowsCount={defaultFundsCount}
                                />
                            </Styled.GridItemBox>
                            <Styled.GridItemBox $gridColumnMd="2" $gridRowMd="1">
                                <Styled.InvestmentStrategyHeadingS>
                                    {t(
                                        `investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.${mode}.changeAllocation`,
                                    )}
                                </Styled.InvestmentStrategyHeadingS>
                            </Styled.GridItemBox>
                            <Styled.GridItemBox $gridColumnMd="2">
                                <Styled.StackWithGap>
                                    {data.fundsWithAllocationRecommendations?.map((fund, index) => (
                                        <EditInvestmentStrategyFundAllocation
                                            key={fund.id}
                                            fundMaxPercentageAllocation={fund.maxPercentageAllocation}
                                            fundName={fund.name}
                                            index={index}
                                        />
                                    ))}

                                    <Styled.CardPaper variant="outlined">
                                        <EditInvestmentStrategyRemainingAllocation />
                                    </Styled.CardPaper>
                                </Styled.StackWithGap>
                            </Styled.GridItemBox>
                        </Styled.GridWrapperBox>

                        <EditInvestmentStrategyConfirmBox
                            fundsWithAllocationRecommendations={data.fundsWithAllocationRecommendations}
                        />

                        <NoChangeWarning ref={warningRef} warning={warning} />
                    </Styled.InvestmentStrategyWrapperStack>
                </form>
            </FormProvider>
        </div>
    );
};

const NoChangeWarning = forwardRef<HTMLDivElement, { warning?: string | null }>(({ warning }, ref) => {
    return (
        <Styled.WarningWrapper ref={ref}>
            {warning && (
                <FormErrorMessage name="noChangeWarning" severity="error">
                    <BodyTextM dangerouslySetInnerHTML={{ __html: warning ?? "" }}></BodyTextM>
                </FormErrorMessage>
            )}
        </Styled.WarningWrapper>
    );
});
